import { useGetRevisionsSchedulePlantQuery } from '@/features/plants/hooks/queries/[plantId]/schedule/use-get-schedule-revision.ts';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionHeader,
  AccordionHeaderTrigger,
  AccordionTrigger,
} from '@/components/ui/accordion.tsx';
import { useGetUsersQuery } from '@/features/users/hooks/queries/get-all-users.tsx';
import { format, formatDistance } from 'date-fns';
import { Badge } from '@/components/ui/badge.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { prettifySchedulerActions } from '@/features/plants/schemas/schedule-plant-revision.schema.utils.ts';
import { TPlantControlSchedulerRevision } from '@/features/plants/schemas/schedule-plant-revision.schema.ts';
import { ActionTooltip } from '@/components/ui/action-tooltip.tsx';
import { Replace } from 'lucide-react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';

export const ScheduleRevision = ({
  plantId,
  scheduleId,
  onReplaceSchedule,
}: {
  plantId?: string;
  scheduleId?: string;
  onReplaceSchedule?: (scheduleRevision: TPlantControlSchedulerRevision) => void;
}) => {
  const { ref, inView } = useInView();

  const { data: users, isLoading: areUsersLoading } = useGetUsersQuery();
  const {
    data,
    isLoading: areHistoriesLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetRevisionsSchedulePlantQuery({
    plantId,
    scheduleId,
  });
  const histories = data?.pages;

  const isLoading = areHistoriesLoading || areUsersLoading;

  useEffect(() => {
    if (inView) {
      fetchNextPage().then();
    }
  }, [inView, fetchNextPage]);

  return (
    <DashboardCard
      className="mt-4 flex min-h-[300px] flex-col gap-4 border border-gray-200/60"
      isLoading={isLoading}
    >
      <h3 className="font-semibold">Schedule Changes</h3>
      {histories && histories.length > 0 ? (
        <ScrollArea className="rounded-md border">
          <ul className="flex h-full max-h-[500px] w-full flex-col">
            {histories?.map((revision, index) => {
              const user = users?.find((u) => u.userId === revision.createdBy);
              const isLatestVersion = index === 0;

              return (
                <li
                  key={`${revision.revisionId}${revision.revisionNumber}`}
                  className="[&:not(:nth-last-child(2))]:border-b"
                >
                  <ScheduleRevisionItem
                    revision={revision}
                    user={user}
                    isLatestVersion={isLatestVersion}
                    onReplaceSchedule={onReplaceSchedule}
                  />
                </li>
              );
            })}
            <li ref={ref}>
              {isFetchingNextPage && (
                <div className="flex w-full items-center justify-center py-2 pb-6">
                  <LoadingSpinner className="text-muted-foreground size-10" />
                </div>
              )}
            </li>
          </ul>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">No schedule changes found</span>
        </div>
      )}
    </DashboardCard>
  );
};

function ScheduleRevisionItem({
  revision,
  user,
  isLatestVersion,
  onReplaceSchedule,
}: {
  revision: TPlantControlSchedulerRevision;
  user?: { userId?: string; firstName?: string; lastName?: string };
  isLatestVersion: boolean;
  onReplaceSchedule?: (scheduleRevision: TPlantControlSchedulerRevision) => void;
}) {
  const username = user ? `${user.firstName} ${user.lastName}` : revision.createdBy;

  const formattedDate = formatDistance(revision.createdAt, new Date(), {
    addSuffix: true,
  });
  const updatedAt = revision.createdAt ? format(revision.createdAt, 'MMM dd, yyyy HH:mm') : '';

  const lines = prettifySchedulerActions(revision?.actions, revision?.snapshot);

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item" className="bg-white">
        <AccordionHeader className="hover:bg-gray-50">
          <div className="flex w-full items-center justify-between gap-4">
            <AccordionHeaderTrigger className="group flex-1 py-3 hover:no-underline">
              <div className="flex flex-col gap-2 text-left">
                <span className="text-muted-foreground inline-block text-xs no-underline">
                  {username} {revision.changeType} {'  '}
                  <span className="text-muted-foreground text-xs" title={updatedAt}>
                    {formattedDate}
                  </span>
                </span>
              </div>
            </AccordionHeaderTrigger>
            <div className="flex items-center gap-2">
              <div>{isLatestVersion && <Badge variant="green">Latest</Badge>}</div>
              <ActionTooltip
                asChild
                align="end"
                alignOffset={-24}
                title={`Replace with revision ${revision.revisionNumber}`}
                clickedTitle={`Replaced with revision ${revision.revisionNumber}`}
                onClick={() => onReplaceSchedule?.(revision)}
              >
                <button className="rounded-md border border-transparent p-2 hover:border-gray-300 hover:bg-white">
                  <Replace className="size-4 text-gray-600" />
                </button>
              </ActionTooltip>
              <AccordionTrigger className="" />
            </div>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div className="flex flex-col gap-2">
            {lines.map((text, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-gray-400"></span>
                <span> {text}</span>
              </div>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
